import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Alert, Spinner, Table } from "react-bootstrap";
import { useMergeLink } from "@mergeapi/react-merge-link";
import axios from "axios";
import { useCookies } from "react-cookie";
import AlertPopup from "../AlertPopup/AlertPopup";
import "./AtsIntegrationTab.css";

// Backend endpoints (dev)
const LINK_TOKEN_URL = "https://api.foveaai.com/evaluate/web/ats/merge/link_token";
const ACCOUNT_TOKEN_URL = "https://api.foveaai.com/evaluate/web/ats/merge/account_token";
const ACTIVE_INTEGRATIONS_URL = "https://api.foveaai.com/evaluate/web/ats/connections";

function AtsIntegrationTab() {
  const [cookies] = useCookies(["AuthorizationToken"]);
  const authHeader = useMemo(
    () => ({ Authorization: cookies.AuthorizationToken }),
    [cookies.AuthorizationToken]
  );

  const [linkToken, setLinkToken] = useState("");
  const [status, setStatus] = useState("idle"); // idle | fetching | ready | connecting | connected | error
  const [error, setError] = useState("");
  const [openAfterToken, setOpenAfterToken] = useState(false);

  // Active integrations state
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeError, setActiveError] = useState("");
  const [integrationsAllowed, setIntegrationsAllowed] = useState(0);
  const [integrationsUsed, setIntegrationsUsed] = useState(0);
  const [integrations, setIntegrations] = useState([]);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState(null);

  const fetchActiveIntegrations = useCallback(async () => {
    try {
      setActiveLoading(true);
      setActiveError("");
      const res = await axios.get(ACTIVE_INTEGRATIONS_URL, { headers: authHeader });
      const data = res?.data || {};
      setIntegrationsAllowed(Number(data.integrations_allowed || 0));
      setIntegrationsUsed(Number(data.integrations_used || 0));
      setIntegrations(Array.isArray(data.integrations) ? data.integrations : []);
    } catch (e) {
      const serverMsg = e?.response?.data?.message;
      let friendly = e?.message || "Failed to load active integrations";
      if (serverMsg === "ats_integration_not_included") {
        friendly = "Your current plan does not include ATS integrations.";
      } else if (serverMsg === "no_active_subscription") {
        friendly = "No active subscription found for your company.";
      } else if (serverMsg === "invalid_company") {
        friendly = "Your account is not associated with a valid company.";
      } else if (serverMsg === "roles_required") {
        friendly = "You need admin or primary role to manage ATS integrations.";
      }
      setActiveError(friendly);
      // Reset list on error
      setIntegrationsAllowed(0);
      setIntegrationsUsed(0);
      setIntegrations([]);
    } finally {
      setActiveLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    void fetchActiveIntegrations();
  }, [fetchActiveIntegrations]);

  const fetchLinkToken = useCallback(async () => {
    try {
      console.debug("[Merge Link] Starting link token fetch", { url: LINK_TOKEN_URL });
      setStatus("fetching");
      setError("");
      // POST per API docs; no body required
      const res = await axios.post(
        LINK_TOKEN_URL,
        {},
        { headers: authHeader }
      );
      console.debug("[Merge Link] Link token response", {
        status: res?.status,
        statusText: res?.statusText,
        data: res?.data,
      });
      const token = res.data?.linkToken || res.data?.link_token || "";
      console.debug("[Merge Link] Extracted link token", { hasToken: Boolean(token), token });
      if (!token) throw new Error("No link token returned");
      setLinkToken(token);
      setStatus("ready");
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || "Failed to create link token";
      console.error("[Merge Link] Link token fetch failed", e);
      setError(msg);
      setStatus("error");
    }
  }, [authHeader]);

  const { open, ready } = useMergeLink({
    linkToken: linkToken || "",
    onSuccess: async (publicToken) => {
      try {
        console.debug("[Merge Link] onSuccess received", { hasPublicToken: Boolean(publicToken), publicToken });
        setStatus("connecting");
        setError("");
        const res = await axios.post(
          ACCOUNT_TOKEN_URL,
          { public_token: publicToken },
          { headers: authHeader }
        );
        // Expect { status: "linked", linkedAccountId?: string }
        if (res.data?.status === "linked") {
          console.debug("[Merge Link] Account token exchange succeeded", { response: res?.data });
          setStatus("connected");
          // Clear used token to avoid reuse on subsequent opens
          setLinkToken("");
          // Refresh active integrations to show the new connection
          void fetchActiveIntegrations();
        } else {
          const msg = res.data?.message || "Unexpected response while linking ATS";
          console.error("[Merge Link] Account token exchange unexpected response", { response: res?.data });
          setError(msg);
          setStatus("error");
          // Clear token so next attempt fetches a new one
          setLinkToken("");
        }
      } catch (e) {
        console.error("[Merge Link] Account token exchange failed", e);
        setError(e?.response?.data?.message || e.message || "Could not finalize ATS connection");
        setStatus("error");
        setLinkToken("");
      }
    },
    onExit: (err) => {
      console.debug("[Merge Link] onExit", { error: err });
      if (err) setError(err.message || "Link flow exited");
      // Reset so next click fetches a fresh token and button re-enables
      setOpenAfterToken(false);
      setLinkToken("");
      setStatus("idle");
    },
    // Extra diagnostics: log every SDK event & error (if supported by the SDK version)
    onEvent: (event, metadata) => {
      try {
        console.debug("[Merge Link] onEvent", { event, metadata });
      } catch (_) {
        // no-op
      }
    },
    onError: (err) => {
      console.error("[Merge Link] onError", err);
      setError(err?.message || "Merge Link initialization error");
      // Clear token to avoid reuse; allow next click to refetch
      setLinkToken("");
      setOpenAfterToken(false);
      setStatus("idle");
    },
  });

  // Debug status transitions
  useEffect(() => {
    console.debug("[Merge Link] Status ->", status);
  }, [status]);

  // Debug when link token and readiness change
  useEffect(() => {
    console.debug("[Merge Link] Link token updated", { hasToken: Boolean(linkToken), token: linkToken });
  }, [linkToken]);

  // If user clicked Connect without a token, fetch then auto-open when token arrives
  useEffect(() => {
    if (openAfterToken && linkToken) {
      console.debug("[Merge Link] Token fetched after click; opening modal now");
      setOpenAfterToken(false);
      // Even if ready is false/undefined in some SDK versions, proceed from user gesture
      try {
        open();
      } catch (e) {
        console.error("[Merge Link] Failed to open after token fetch", e);
      }
    }
  }, [openAfterToken, linkToken, open]);

  useEffect(() => {
    console.debug("[Merge Link] SDK ready state changed", { ready });
  }, [ready]);

  // Warn if the SDK didn't become ready after receiving a token
  useEffect(() => {
    if (!linkToken) return;
    const t1 = setTimeout(() => {
      if (!ready) {
        console.warn(
          "[Merge Link] SDK still not ready 3s after token. Possible causes: CSP blocking cdn.merge.dev/link.merge.dev, ad blockers, or invalid token."
        );
        if (typeof window !== "undefined") {
          console.debug("[Merge Link] Window diagnostics", {
            location: window.location?.href,
            mergeGlobals: {
              MergeLink: window.MergeLink,
            },
          });
        }
      }
    }, 3000);
    const t2 = setTimeout(() => {
      if (!ready) {
        console.warn(
          "[Merge Link] SDK still not ready after 10s. Check Network tab for blocked requests to cdn.merge.dev and link.merge.dev, and review CSP."
        );
      }
    }, 10000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [linkToken, ready]);

  // Button can be clicked even without a token (we'll fetch on demand).
  const canClick = status !== "fetching" && status !== "connecting";
  const canAddIntegration = integrationsAllowed > integrationsUsed;

  return (
    <div className="register-form-container">
      <div style={{ maxWidth: 800, width: "100%" }}>
      <h3>Applicant Tracking System (ATS)</h3>
      <p className="mb-2">
        Connect your ATS to sync jobs, candidates, and applications.
      </p>

      {status === "fetching" && (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Preparing connection…</span>
        </div>
      )}

      {status === "connected" && (
        <Alert variant="success" className="mt-2">
          ATS connected successfully.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mt-2">
          {error}
        </Alert>
      )}

      {activeError && (
        <Alert variant="warning" className="mt-2">
          {activeError}
        </Alert>
      )}

  <div className="mt-4">
        <div className="d-flex justify-content-between align-items-end mb-2">
          <h5 className="mb-0">Active ATS Integrations</h5>
          <small className="text-muted">
            {activeLoading ? "Loading…" : `${integrationsUsed} of ${integrationsAllowed} used`}
          </small>
        </div>

        {activeLoading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading active integrations…</span>
          </div>
        ) : (
          <Table striped size="sm">
            <tbody>
              {integrations.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-center text-muted">
                    No active integrations yet.
                  </td>
                </tr>
              ) : (
                integrations.map((i) => (
                  <tr key={i.id}>
                    <td className="align-middle text-start p-3">
                      {i.integration_logo_url ? (
                        <img
                          src={i.integration_logo_url}
                          alt={i.integration_name || i.ats_name || "Integration"}
                          style={{ width: 196, height: 64, objectFit: 'contain' }}
                        />
                      ) : (
                        i.integration_name || i.ats_name || "-"
                      )}
                    </td>
                    <td className="text-center align-middle p-3">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-danger"
                        title="Remove integration"
                        onClick={() => {
                          setConnectionToDelete(i);
                          setShowDeleteModal(true);
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          aria-hidden="true"
                          focusable="false"
                        >
                          <path d="M6 1h4l1 1h3v2H2V2h3l1-1z" />
                          <path d="M3 5h10l-1 9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2L3 5z" />
                        </svg>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

  <div className="mt-3 d-flex gap-2">
        {canAddIntegration && (
          <Button
            variant="primary"
            disabled={!canClick}
            onClick={() => {
              const sdkReady = ready === undefined ? true : !!ready;
              console.debug("[Merge Link] Connect button clicked", { ready, sdkReady, hasToken: Boolean(linkToken), status });
              if (!canClick) return;
              // Always fetch a fresh token for each attempt; auto-open when it arrives
              setOpenAfterToken(true);
              setLinkToken("");
              void fetchLinkToken();
            }}
          >
            {status === "connecting" ? "Finishing…" : "Connect ATS"}
          </Button>
        )}
      </div>

      <AlertPopup
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          setShowDeleteModal(false);
          if (connectionToDelete?.id) {
            try {
              const response = await axios.delete(`https://api.foveaai.com/evaluate/web/ats/merge/connection/${connectionToDelete.id}`, {
                headers: authHeader
              });
              
              const { status, message, merge_delete_success } = response.data;
              
              if (status === "deleted") {
                setActiveError(""); // Clear any previous errors
                // Refresh the integrations list after successful deletion
                await fetchActiveIntegrations();
              } else if (status === "partially_deleted") {
                setActiveError(`Warning: ${message}`);
                // Still refresh since connection was deactivated locally
                await fetchActiveIntegrations();
              } else {
                // Handle other error statuses
                setActiveError(message || "Failed to delete ATS connection.");
              }
              
              // Check merge_delete_success for additional error handling
              if (merge_delete_success === false && (status === "deleted" || status === "partially_deleted")) {
                // This case is already handled by partially_deleted status above
                // but we keep this check for completeness
              }
            } catch (error) {
              console.error("Failed to delete connection:", error);
              const serverMsg = error?.response?.data?.message;
              const status = error?.response?.data?.status;
              let friendlyError = "Failed to remove ATS integration. Please try again.";
              
              if (status === "connection_not_found") {
                friendlyError = serverMsg || "ATS connection not found or no longer accessible.";
              } else if (status === "no_account_token") {
                friendlyError = serverMsg || "Cannot remove integration: no account token found.";
              } else if (status === "merge_client_failed") {
                friendlyError = serverMsg || "Failed to connect to ATS service. Please try again.";
              } else if (status === "db_update_failed") {
                friendlyError = serverMsg || "Failed to update connection status. Please contact support.";
              } else if (status === "invalid_id") {
                friendlyError = serverMsg || "Invalid connection ID.";
              } else if (status === "no_company") {
                friendlyError = serverMsg || "User is not associated with a company.";
              } else if (serverMsg) {
                friendlyError = serverMsg;
              }
              
              setActiveError(friendlyError);
            }
          }
        }}
        title="Confirm Delete"
        type="alert"
        heading="Remove ATS Integration"
        body={`Are you sure you want to remove the ${connectionToDelete?.integration_name || connectionToDelete?.ats_name || 'ATS'} integration? This action cannot be undone.`}
        buttonText="Remove"
      />
      </div>
    </div>
  );
}

export default AtsIntegrationTab;
